/**
 * lib/experiences/cityMeta.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * City hero visual metadata for the Experiences feature.
 *
 * Exports:
 *   CityHeroMeta   — type for one city's hero visuals
 *   CITY_HERO_META — record keyed by IATA (TYO, BCN, LIS, MEX, CDG)
 *
 * Only covers cities supported by the Experiences engine.
 * Keyed by the same IATA codes used in lib/cities/index.ts.
 *
 * Used by:
 *   routes/experiences.tsx — CityHero component
 */

// ─── Type ─────────────────────────────────────────────────────────────────────

export interface CityHeroMeta {
  /** Short city name shown in the large hero typography */
  heroName:   string;
  /** CSS gradient for the hero background */
  gradient:   string;
  /** CSS color for the first atmospheric orb */
  orbColor1:  string;
  /** CSS color for the second atmospheric orb */
  orbColor2:  string;
  /** oklch accent color for the IATA watermark text */
  textAccent: string;
}

// ─── Registry ─────────────────────────────────────────────────────────────────

export const CITY_HERO_META: Readonly<Record<string, CityHeroMeta>> = {
  TYO: {
    heroName:   "TOKYO",
    gradient:   "linear-gradient(145deg, oklch(0.975 0.016 290) 0%, oklch(0.962 0.025 274) 100%)",
    orbColor1:  "oklch(0.60 0.18 285 / 0.20)",
    orbColor2:  "oklch(0.55 0.12 260 / 0.15)",
    textAccent: "oklch(0.50 0.16 285)",
  },
  BCN: {
    heroName:   "BARCELONA",
    gradient:   "linear-gradient(145deg, oklch(0.985 0.026 56) 0%, oklch(0.972 0.022 40) 100%)",
    orbColor1:  "oklch(0.72 0.14 56 / 0.22)",
    orbColor2:  "oklch(0.68 0.10 38 / 0.16)",
    textAccent: "oklch(0.56 0.14 50)",
  },
  LIS: {
    heroName:   "LISBOA",
    gradient:   "linear-gradient(145deg, oklch(0.985 0.024 32) 0%, oklch(0.972 0.020 20) 100%)",
    orbColor1:  "oklch(0.70 0.13 32 / 0.22)",
    orbColor2:  "oklch(0.65 0.10 20 / 0.16)",
    textAccent: "oklch(0.52 0.13 28)",
  },
  MEX: {
    heroName:   "CDMX",
    gradient:   "linear-gradient(145deg, oklch(0.985 0.028 52) 0%, oklch(0.972 0.026 64) 100%)",
    orbColor1:  "oklch(0.72 0.16 52 / 0.22)",
    orbColor2:  "oklch(0.68 0.14 65 / 0.16)",
    textAccent: "oklch(0.54 0.16 48)",
  },
  CDG: {
    heroName:   "PARIS",
    gradient:   "linear-gradient(145deg, oklch(0.978 0.018 242) 0%, oklch(0.966 0.014 228) 100%)",
    orbColor1:  "oklch(0.55 0.11 238 / 0.20)",
    orbColor2:  "oklch(0.50 0.08 220 / 0.14)",
    textAccent: "oklch(0.42 0.10 235)",
  },
  // Madrid — warm terracotta / Spanish vermillion palette
  MAD: {
    heroName:   "MADRID",
    gradient:   "linear-gradient(145deg, oklch(0.984 0.026 30) 0%, oklch(0.972 0.022 22) 100%)",
    orbColor1:  "oklch(0.62 0.19 27 / 0.20)",
    orbColor2:  "oklch(0.70 0.14 48 / 0.16)",
    textAccent: "oklch(0.50 0.18 25)",
  },

  // ── Japan ─────────────────────────────────────────────────────────────────

  KYO: {
    heroName:   "KIOTO",
    gradient:   "linear-gradient(145deg, oklch(0.978 0.020 358) 0%, oklch(0.966 0.016 348) 100%)",
    orbColor1:  "oklch(0.62 0.15 358 / 0.20)",
    orbColor2:  "oklch(0.58 0.10 344 / 0.14)",
    textAccent: "oklch(0.46 0.12 356)",
  },
  OSA: {
    heroName:   "OSAKA",
    gradient:   "linear-gradient(145deg, oklch(0.982 0.022 50) 0%, oklch(0.970 0.018 40) 100%)",
    orbColor1:  "oklch(0.72 0.16 52 / 0.22)",
    orbColor2:  "oklch(0.68 0.12 42 / 0.16)",
    textAccent: "oklch(0.54 0.16 50)",
  },
  HKN: {
    heroName:   "HAKONE",
    gradient:   "linear-gradient(145deg, oklch(0.980 0.016 160) 0%, oklch(0.968 0.012 152) 100%)",
    orbColor1:  "oklch(0.56 0.12 162 / 0.20)",
    orbColor2:  "oklch(0.52 0.08 148 / 0.14)",
    textAccent: "oklch(0.42 0.10 158)",
  },
  KNZ: {
    heroName:   "KANAZAWA",
    gradient:   "linear-gradient(145deg, oklch(0.983 0.022 28) 0%, oklch(0.970 0.018 20) 100%)",
    orbColor1:  "oklch(0.68 0.14 28 / 0.22)",
    orbColor2:  "oklch(0.62 0.10 20 / 0.16)",
    textAccent: "oklch(0.50 0.14 24)",
  },

  // ── Spain / Andalusia ─────────────────────────────────────────────────────

  SEV: {
    heroName:   "SEVILLA",
    gradient:   "linear-gradient(145deg, oklch(0.985 0.026 46) 0%, oklch(0.972 0.022 38) 100%)",
    orbColor1:  "oklch(0.72 0.18 48 / 0.22)",
    orbColor2:  "oklch(0.68 0.14 36 / 0.16)",
    textAccent: "oklch(0.52 0.18 44)",
  },
  GRA: {
    heroName:   "GRANADA",
    gradient:   "linear-gradient(145deg, oklch(0.980 0.020 148) 0%, oklch(0.968 0.016 140) 100%)",
    orbColor1:  "oklch(0.56 0.14 148 / 0.20)",
    orbColor2:  "oklch(0.52 0.10 140 / 0.14)",
    textAccent: "oklch(0.42 0.12 145)",
  },
  COR: {
    heroName:   "CÓRDOBA",
    gradient:   "linear-gradient(145deg, oklch(0.984 0.024 44) 0%, oklch(0.972 0.020 36) 100%)",
    orbColor1:  "oklch(0.70 0.16 46 / 0.20)",
    orbColor2:  "oklch(0.65 0.12 36 / 0.14)",
    textAccent: "oklch(0.50 0.15 42)",
  },
  AGP: {
    heroName:   "MÁLAGA",
    gradient:   "linear-gradient(145deg, oklch(0.978 0.018 208) 0%, oklch(0.966 0.014 198) 100%)",
    orbColor1:  "oklch(0.58 0.12 205 / 0.20)",
    orbColor2:  "oklch(0.54 0.08 195 / 0.14)",
    textAccent: "oklch(0.44 0.10 205)",
  },
  CDZ: {
    heroName:   "CÁDIZ",
    gradient:   "linear-gradient(145deg, oklch(0.976 0.016 200) 0%, oklch(0.964 0.012 190) 100%)",
    orbColor1:  "oklch(0.56 0.12 200 / 0.20)",
    orbColor2:  "oklch(0.52 0.08 190 / 0.14)",
    textAccent: "oklch(0.42 0.10 198)",
  },
  TOL: {
    heroName:   "TOLEDO",
    gradient:   "linear-gradient(145deg, oklch(0.983 0.022 36) 0%, oklch(0.970 0.018 28) 100%)",
    orbColor1:  "oklch(0.66 0.15 38 / 0.20)",
    orbColor2:  "oklch(0.60 0.11 28 / 0.14)",
    textAccent: "oklch(0.48 0.14 34)",
  },

  // ── Portugal ──────────────────────────────────────────────────────────────

  OPO: {
    heroName:   "OPORTO",
    gradient:   "linear-gradient(145deg, oklch(0.983 0.022 28) 0%, oklch(0.970 0.018 22) 100%)",
    orbColor1:  "oklch(0.68 0.14 28 / 0.22)",
    orbColor2:  "oklch(0.62 0.10 20 / 0.16)",
    textAccent: "oklch(0.50 0.14 24)",
  },
  CBM: {
    heroName:   "COIMBRA",
    gradient:   "linear-gradient(145deg, oklch(0.980 0.018 270) 0%, oklch(0.968 0.014 260) 100%)",
    orbColor1:  "oklch(0.58 0.12 268 / 0.20)",
    orbColor2:  "oklch(0.54 0.08 258 / 0.14)",
    textAccent: "oklch(0.44 0.10 265)",
  },
  FAO: {
    heroName:   "FARO",
    gradient:   "linear-gradient(145deg, oklch(0.978 0.018 195) 0%, oklch(0.966 0.014 185) 100%)",
    orbColor1:  "oklch(0.60 0.13 195 / 0.20)",
    orbColor2:  "oklch(0.56 0.09 182 / 0.14)",
    textAccent: "oklch(0.44 0.11 192)",
  },
  DOV: {
    heroName:   "DOURO",
    gradient:   "linear-gradient(145deg, oklch(0.980 0.020 148) 0%, oklch(0.968 0.016 138) 100%)",
    orbColor1:  "oklch(0.56 0.13 148 / 0.20)",
    orbColor2:  "oklch(0.52 0.09 138 / 0.14)",
    textAccent: "oklch(0.42 0.11 145)",
  },

  // ── Benelux ───────────────────────────────────────────────────────────────

  BGE: {
    heroName:   "BRUJAS",
    gradient:   "linear-gradient(145deg, oklch(0.980 0.016 265) 0%, oklch(0.968 0.012 255) 100%)",
    orbColor1:  "oklch(0.60 0.12 268 / 0.20)",
    orbColor2:  "oklch(0.56 0.08 256 / 0.14)",
    textAccent: "oklch(0.46 0.10 263)",
  },

  // ── Italy ─────────────────────────────────────────────────────────────────

  ROM: {
    heroName:   "ROMA",
    gradient:   "linear-gradient(145deg, oklch(0.985 0.024 42) 0%, oklch(0.972 0.020 34) 100%)",
    orbColor1:  "oklch(0.68 0.14 44 / 0.22)",
    orbColor2:  "oklch(0.62 0.10 34 / 0.16)",
    textAccent: "oklch(0.52 0.14 40)",
  },
  FLR: {
    heroName:   "FLORENCIA",
    gradient:   "linear-gradient(145deg, oklch(0.984 0.024 50) 0%, oklch(0.972 0.020 42) 100%)",
    orbColor1:  "oklch(0.70 0.14 52 / 0.22)",
    orbColor2:  "oklch(0.64 0.10 42 / 0.16)",
    textAccent: "oklch(0.54 0.14 48)",
  },
  VCE: {
    heroName:   "VENECIA",
    gradient:   "linear-gradient(145deg, oklch(0.978 0.018 208) 0%, oklch(0.966 0.014 198) 100%)",
    orbColor1:  "oklch(0.58 0.12 206 / 0.20)",
    orbColor2:  "oklch(0.54 0.08 196 / 0.14)",
    textAccent: "oklch(0.44 0.10 204)",
  },
  NAP: {
    heroName:   "NÁPOLES",
    gradient:   "linear-gradient(145deg, oklch(0.982 0.022 38) 0%, oklch(0.970 0.018 30) 100%)",
    orbColor1:  "oklch(0.68 0.16 40 / 0.22)",
    orbColor2:  "oklch(0.62 0.12 30 / 0.16)",
    textAccent: "oklch(0.52 0.15 36)",
  },
  // Milan — steel grey-blue, fashion-city palette
  MXP: {
    heroName:   "MILÁN",
    gradient:   "linear-gradient(145deg, oklch(0.980 0.012 240) 0%, oklch(0.968 0.010 228) 100%)",
    orbColor1:  "oklch(0.52 0.08 235 / 0.20)",
    orbColor2:  "oklch(0.48 0.06 222 / 0.14)",
    textAccent: "oklch(0.40 0.08 232)",
  },
};
